import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
// mem - 토큰 재발급 위함
import mem from 'mem';

import router from '@/router';
import { usePopupStore } from '@/stores/popup';
import { useTokenStore } from '@/stores/token';
const instance = axios.create({
	baseURL: import.meta.env.VITE_BASE_URL,
	headers: {
		ClientSecret: import.meta.env.VITE_CLIENT_SECRET,
	},
});

// 토큰 재발급 함수
const requestToken = mem(
	async (): Promise<string | void> => {
		const tokenStore = useTokenStore();
		try {
			const tokenRes = await instance.post('/v1/front/requestToken', {
				refresh_token: tokenStore.getRefreshToken,
			});
			const accessToken = tokenRes.data?.data.access_token;
			if (accessToken) {
				tokenStore.setAccessToken(accessToken);
			}
			return accessToken;
		} catch (e) {
			// 네트워크 에러는 제외
			if (e instanceof AxiosError && e.code !== 'ERR_NETWORK') {
				alert('토큰 오류, 다시 로그인해주세요.');
				tokenStore.resetToken();
				router.push('/');
			}
		}
	},
	{ maxAge: 1000 }
);

instance.interceptors.request.use(
	// interceptor를 통해 백엔드 API로부터 받은 값을 먼저 처리함
	(request: InternalAxiosRequestConfig) => {
		const tokenStore = useTokenStore();
		request.headers.Authorization = tokenStore.isLogin ? tokenStore.getAccessToken : null;
		return request;
	},
	(error) => {
		// 2xx 외의 범위에 있는 상태코드는 여기에서 실행됨
		return Promise.reject(error);
	}
);

instance.interceptors.response.use(
	async (response: AxiosResponse) => {
		const tokenStore = useTokenStore();
		const popupStore = usePopupStore();
		const { config, data } = response;
		const error = response?.data?.error;

		if (error) {
			const errorCode = Number(error.code);
			console.error(errorCode, error.message); // 에러 콘솔로 찍기

			// 리포트 무료 조회분
			if (errorCode === 51) {
				router.back();
				setTimeout(() => {
					popupStore.openPopup({
						content: '구독 시 투자리포트를 자유롭게 확인하실 수 있습니다.',
						subtext: '미 구독 시 투자리포트는 일 2회 열람 가능하며,<br/>열람 횟수는 매일 00시 업데이트 됩니다.',
						// 구독 버튼 임시 주석 261023
						// confirmText: '구독하기',
						// confirm: () => {
						// 	router.push('/payment');
						// },
					});
				}, 100);
			}

			if (errorCode === 84 || errorCode === 91) {
				alert(errorCode === 84 ? '세션이 만료되어 로그아웃됩니다.' : '다시 로그인을 진행해 주세요.');
				tokenStore.resetToken();
				router.push('/auth/login');
			}
			// 토큰 만료 시 토큰 재발급
			else if (errorCode === 97) {
				const accessToken = await requestToken(); // 토큰 재발급
				// 새 토큰이 정상적으로 발급되면 토큰 저장 후, 중단된 요청을 재요청
				if (accessToken) {
					config.headers.Authorization = accessToken;
					return instance(config);
				}
			}
			// 토큰 불일치 시 로그아웃
		} else {
			console.log(`${config.url} \n`, data); // 데이터 콘솔로 찍기
		}
		return response;
	},

	(error) => {
		console.error(`${error.config.url} \n`, error);

		// 2xx 외의 범위에 있는 상태코드는 여기에서 실행됨
		if (error.code === 'ERR_NETWORK') {
			alert('일시적인 네트워크 연결 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
		} else if (error.code === 'ERR_BAD_RESPONSE') {
			alert('서버가 응답하지 않습니다. 잠시 후 시도해주세요');
		} else if (error.code === 'ECONNABORTED') {
			alert('요청시간을 초과했습니다. 잠시 후 시도해주세요');
		} else if (error.code === 'ERR_BAD_REQUEST') {
			alert('올바르지 않은 요청입니다. 잠시 후 시도해주세요');
		} else {
			alert(error.code);
		}

		return Promise.reject(error);
	}
);

export default instance;
export const api = {
	get: <T>(url: string, params?: any, config?: AxiosRequestConfig) => instance.get<T>(url, { params, ...config }),
	post: <T>(url: string, data?: any, config?: AxiosRequestConfig) => instance.post<T>(url, data, config),
	put: <T>(url: string, data?: any, config?: AxiosRequestConfig) => instance.put<T>(url, data, config),
	patch: <T>(url: string, data?: any, config?: AxiosRequestConfig) => instance.patch<T>(url, data, config),
	delete: <T>(url: string, params?: any, config?: AxiosRequestConfig) => instance.delete<T>(url, { params, ...config }),
};
